import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { reviewsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Star, MapPin, CheckCircle } from 'lucide-react';

const emptyReview = {
  name: '',
  location: '',
  text: '',
  verified: true,
};

export const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [formData, setFormData] = useState(emptyReview);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await reviewsAPI.getAll();
      setReviews(response.data);
    } catch (e) {
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingReview(null);
    setFormData(emptyReview);
    setDialogOpen(true);
  };

  const openEditDialog = (review) => {
    setEditingReview(review);
    setFormData(review);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingReview) {
        await reviewsAPI.update(editingReview.id, formData);
        toast.success('Review updated!');
      } else {
        await reviewsAPI.create(formData);
        toast.success('Review created!');
      }
      setDialogOpen(false);
      fetchReviews();
    } catch (e) {
      toast.error('Failed to save review');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    try {
      await reviewsAPI.delete(id);
      toast.success('Review deleted!');
      fetchReviews();
    } catch (e) {
      toast.error('Failed to delete review');
    }
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-reviews">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Reviews</h1>
          <p className="mt-2 text-slate-600">Manage customer testimonials</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreateDialog} data-testid="add-review-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Review
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingReview ? 'Edit Review' : 'Add Review'}</DialogTitle>
              <DialogDescription>Add customer testimonials</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Customer Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => updateField('name', e.target.value)}
                    placeholder="John D."
                    data-testid="review-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => updateField('location', e.target.value)}
                    placeholder="New York, NY"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Review Text</Label>
                <Textarea
                  value={formData.text}
                  onChange={(e) => updateField('text', e.target.value)}
                  placeholder="The product arrived quickly and I love it..."
                  rows={4}
                  data-testid="review-text-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.verified}
                  onCheckedChange={(checked) => updateField('verified', checked)}
                />
                <Label>Verified Purchase</Label>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving} data-testid="save-review-btn">
                  {saving ? 'Saving...' : 'Save Review'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {reviews.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Star className="w-12 h-12 mx-auto text-slate-400" />
            <p className="mt-4 text-slate-600">No reviews yet. Add your first review!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {reviews.map((review) => (
            <Card key={review.id}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900">{review.name}</h3>
                      {review.verified && (
                        <span className="flex items-center gap-1 text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded">
                          <CheckCircle className="w-3 h-3" />
                          Verified
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                      <MapPin className="w-3 h-3" />
                      {review.location}
                    </p>
                    <p className="mt-3 text-slate-600 italic">"{review.text}"</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-4 pt-4 border-t">
                  <Button size="sm" variant="outline" onClick={() => openEditDialog(review)}>
                    <Edit className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => handleDelete(review.id)}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminReviews;
